import React, { useState } from 'react';
import { useAvatar } from '../context/AvatarContext';
import { LockIcon } from 'lucide-react';
// Avatar Selection component
export const AvatarSelection = ({
  onSelectAvatar,
  onCursorChange
}) => {
  const {
    allAvatars
  } = useAvatar();
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [isSelectionComplete, setIsSelectionComplete] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(null);
  // Handle avatar selection
  const handleSelectAvatar = avatar => {
    if (avatar.locked) return; // Don't allow selection of locked avatars
    setSelectedAvatar(avatar);
    // Store selection in localStorage
    localStorage.setItem('selectedAvatar', avatar.id);
    // Apply root CSS variables
    document.documentElement.style.setProperty('--primary', avatar.accent);
    document.documentElement.style.setProperty('--primary-dark', avatar.secondaryAccent);
    // Complete selection and notify parent
    onSelectAvatar(avatar);
    setIsSelectionComplete(true);
    // Scroll to top of page after a brief delay
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 800);
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#030508] bg-opacity-95" data-id="element-172">
      <style jsx global data-id="element-173">{`
        .avatar-card {
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        .avatar-card:hover:not(.locked) {
          transform: translateY(-6px);
        }
        .avatar-card.selected {
          transform: translateY(-6px) scale(1.05);
        }
        .avatar-image-container {
          position: relative;
          overflow: hidden;
          border-radius: 6px;
          transition: all 0.3s ease;
        }
        .avatar-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            transparent 50%,
            rgba(0, 0, 0, 0.8) 100%
          );
          opacity: 0.7;
          transition: opacity 0.3s ease;
        }
        .avatar-card:hover:not(.locked) .avatar-overlay {
          opacity: 0.4;
        }
        .avatar-color-filter {
          position: absolute;
          inset: 0;
          mix-blend-mode: color;
          opacity: 0.4;
          transition: opacity 0.3s ease;
        }
        .avatar-card:hover:not(.locked) .avatar-color-filter {
          opacity: 0.6;
        }
        .scan-line {
          position: absolute;
          height: 2px;
          left: 0;
          right: 0;
          background: linear-gradient(
            90deg,
            transparent,
            var(--accent-color),
            transparent
          );
          animation: scan 3s linear infinite;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .avatar-card:hover:not(.locked) .scan-line {
          opacity: 1;
        }
        .avatar-info {
          opacity: 0.7;
          transition:
            opacity 0.3s ease,
            transform 0.3s ease;
          transform: translateY(4px);
        }
        .avatar-card:hover:not(.locked) .avatar-info {
          opacity: 1;
          transform: translateY(0);
        }
        @keyframes scan {
          0% {
            top: -5%;
          }
          100% {
            top: 105%;
          }
        }
        .avatar-selection-title {
          background: linear-gradient(90deg, #a0e0e8, #d3b2d8);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          text-shadow: 0 0 15px rgba(160, 224, 232, 0.4);
        }
        @keyframes pulse-indicator {
          0%,
          100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }
        .entity-indicator {
          animation: pulse-indicator 2s infinite;
        }
        .avatar-glow {
          position: absolute;
          inset: 0;
          filter: blur(20px);
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: -1;
          border-radius: inherit;
        }
        .avatar-card:hover:not(.locked) .avatar-glow {
          opacity: 0.6;
        }
        .rainbow-halo {
          position: absolute;
          inset: -10px;
          background: conic-gradient(
            from 180deg at 50% 50%,
            #e8b0b0,
            #e8e4b0,
            #b0e8c4,
            #a0e0e8,
            #b6c7e8,
            #d3b2d8,
            #e8b0b0
          );
          border-radius: 12px;
          opacity: 0;
          filter: blur(15px);
          transition: opacity 0.5s ease;
          z-index: -2;
          animation: rotate-halo 12s linear infinite;
        }
        .avatar-card:hover:not(.locked) .rainbow-halo {
          opacity: 0.25;
        }
        @keyframes rotate-halo {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .interface-corner {
          position: absolute;
          width: 10px;
          height: 10px;
          opacity: 0;
          transition: all 0.3s ease;
        }
        .corner-tl {
          top: 0;
          left: 0;
          border-top: 1px solid;
          border-left: 1px solid;
        }
        .corner-tr {
          top: 0;
          right: 0;
          border-top: 1px solid;
          border-right: 1px solid;
        }
        .corner-bl {
          bottom: 0;
          left: 0;
          border-bottom: 1px solid;
          border-left: 1px solid;
        }
        .corner-br {
          bottom: 0;
          right: 0;
          border-bottom: 1px solid;
          border-right: 1px solid;
        }
        .avatar-card:hover:not(.locked) .interface-corner {
          opacity: 1;
          width: 15px;
          height: 15px;
        }
        .locked-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }
        .lock-icon {
          background: rgba(0, 0, 0, 0.5);
          border-radius: 50%;
          padding: 6px;
          margin-bottom: 5px;
          border: 1px solid rgba(160, 160, 160, 0.5);
          box-shadow: 0 0 15px rgba(160, 160, 160, 0.5);
        }
        @keyframes glitch {
          0% {
            transform: translate(0);
          }
          20% {
            transform: translate(-2px, 2px);
          }
          40% {
            transform: translate(-2px, -2px);
          }
          60% {
            transform: translate(2px, 2px);
          }
          80% {
            transform: translate(2px, -2px);
          }
          100% {
            transform: translate(0);
          }
        }
        .locked-text {
          font-family: 'JetBrains Mono', monospace;
          color: #a0a0a0;
          text-shadow: 0 0 5px #a0a0a0;
          animation: glitch 0.3s infinite;
          letter-spacing: 1px;
        }
        .enigma-filter {
          position: absolute;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.2);
          mix-blend-mode: saturation;
          z-index: 5;
        }
      `}</style>
      {!isSelectionComplete ? <div className="max-w-4xl w-full mx-auto p-5 glass border border-[#1a2a40]/50 rounded-lg" data-id="element-174">
          <div className="text-center mb-6" data-id="element-175">
            <h2 className="avatar-selection-title text-3xl md:text-4xl font-bold mb-2 font-heading" data-id="element-176">
              SELECT YOUR ENTITY
            </h2>
            <p className="text-[#a0c0e0] text-sm font-mono" data-id="element-177">
              Choose a vessel to experience Cheryl Qian's biological distortion
              experiments.
              <br data-id="element-178" />
              Each entity provides a unique interface to the same horrifying
              beauty.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-179">
            {allAvatars.map(avatarOption => <button key={avatarOption.id} className={`
                  avatar-card relative overflow-hidden rounded-lg 
                  glass border border-[#1a2a40]/50 
                  hover:border-[${avatarOption.accent}]/70 
                  transition-all duration-500 group
                  ${avatarOption.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                `} onClick={() => {
          if (!avatarOption.locked) {
            handleSelectAvatar(avatarOption);
          }
        }} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} disabled={avatarOption.locked} data-id="element-180">
                <div className="p-4 flex flex-col items-center" data-id="element-181">
                  <div className="avatar-image-container w-24 h-24 rounded-full overflow-hidden mb-4 relative" data-id="element-182">
                    <img src={avatarOption.image} alt={avatarOption.name} className={`w-full h-full object-cover ${avatarOption.locked ? 'filter grayscale' : ''}`} data-id="element-183" />
                    {avatarOption.locked && <div className="absolute inset-0 flex items-center justify-center bg-black/50" data-id="element-184">
                        <LockIcon size={24} className="text-white" data-id="element-185" />
                      </div>}
                  </div>
                  <h3 className="text-[#f0f6ff] font-bold text-lg mb-1 font-heading" data-id="element-186">
                    {avatarOption.name}
                  </h3>
                  <p className="text-[#a0c0e0] text-xs text-center font-mono" data-id="element-187">
                    {avatarOption.description}
                  </p>
                </div>
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-[${avatarOption.accent}] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ${avatarOption.locked ? 'hidden' : ''}`} data-id="element-188"></div>
              </button>)}
          </div>
          <div className="mt-6 text-center" data-id="element-189">
            <p className="text-[#8a9cb9] text-xs font-mono" data-id="element-190">
              Click on an entity to select and proceed to the experience.
            </p>
          </div>
        </div> : <div className="text-center p-6 glass border border-[#1a2a40]/50 rounded-lg max-w-xs" data-id="element-191">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full glass border flex items-center justify-center" style={{
        borderColor: `${selectedAvatar?.accent}50`
      }} data-id="element-192">
            <div className="w-6 h-6 rounded-full animate-pulse" style={{
          backgroundColor: selectedAvatar?.accent
        }} data-id="element-193"></div>
          </div>
          <h3 className="text-xl font-bold mb-2 font-heading" style={{
        color: selectedAvatar?.accent,
        textShadow: `0 0 8px ${selectedAvatar?.accent}80`
      }} data-id="element-194">
            INITIALIZATION COMPLETE
          </h3>
          <p className="text-[#a0c0e0] text-sm font-mono mb-4" data-id="element-195">
            {selectedAvatar?.systemMessage || 'ENTITY SELECTED'}
          </p>
          <div className="entity-indicator text-xs font-mono" style={{
        color: selectedAvatar?.accent
      }} data-id="element-196">
            REDIRECTING TO EXPERIENCE...
          </div>
        </div>}
    </div>;
};